/**
 * The picture of a camera whose video comes over the data channel (see VideoMessage): a
 * WebCodecs decoder feeding a generated MediaStreamTrack, so the tile's <video> and everything
 * that reads it stay as they are for an RTP camera. The decoding runs in a worker, out of the
 * way of the page's own work (see dataChannelVideo.worker.ts); this thread only passes the
 * frames over.
 */

import type { DataChannelVideoTrack } from '@/types/player';

import { WorkerVideoTrack } from './workerVideoTrack';

/** The streams generated here, whose frames carry their RTP timestamp in mediaTime. */
const generatedStreams = new WeakSet<MediaStream>();

/** Whether a stream was generated here, with the RTP timestamp of each frame in its mediaTime. */
export function isDataChannelStream(stream: MediaStream): boolean {
    return generatedStreams.has(stream);
}

/** The track of a camera's data channel video; null in a browser without workers. */
export function createDataChannelVideoTrack(): DataChannelVideoTrack | null {
    if (typeof Worker !== 'function') { return null; }
    const track = new WorkerVideoTrack();
    generatedStreams.add(track.stream);
    return track;
}
